import type { OwnerTaskPlace } from "@szyos/contracts";

/**
 * Schritte für den Owner aus dem Ergebnis eines Laufs (23.10.2026, Kriterium 6 in secondBRAIN): Ein Agent baut
 * oft nur das Werkzeug, die Wirkung entsteht erst, wenn der Owner es auf seinem Gerät, Server oder Konto
 * ausführt. Der Agent meldet diese Schritte in einem festen Block; szyOS legt sie unter „Für dich zu tun“ an und
 * nennt den Auftrag erst danach erledigt.
 */
export const OWNER_STEPS_MARKER = "SZYOS-DEINE-SCHRITTE:";

export interface OwnerSteps {
  title: string;
  place: OwnerTaskPlace;
  steps: string[];
}

const PLACES: readonly OwnerTaskPlace[] = ["vps", "plesk", "pc", "claude_app", "szyos", "other"];
const MAX_STEPS = 20;

/** Anweisung an schreibende Läufe, wie sie Schritte für den Owner melden. */
export const OWNER_STEPS_INSTRUCTION = [
  "Braucht das Ergebnis danach noch einen Schritt, den nur der Owner selbst machen kann (etwa ein Skript auf seinem",
  "PC oder Server ausführen, etwas in DSM, Plesk oder einem Konto einrichten), beende deine Zusammenfassung mit",
  `diesem Block: eine Zeile „${OWNER_STEPS_MARKER} <kurzer Titel>“, dann „Ort: pc“ (oder vps, plesk, claude_app,`,
  "other), dann je Schritt eine Zeile „- <Schritt>“, Befehle als „- $ <Befehl>“. Behaupte nie, etwas sei auf seinen",
  "Geräten eingerichtet, das du nicht selbst ausgeführt hast.",
].join(" ");

/** Liest alle Blöcke aus einer Zusammenfassung; ohne Block eine leere Liste. */
export function parseOwnerSteps(summary: string | null | undefined): OwnerSteps[] {
  if (!summary?.includes(OWNER_STEPS_MARKER)) return [];
  const result: OwnerSteps[] = [];
  let current: OwnerSteps | null = null;
  for (const raw of summary.split(/\r?\n/)) {
    const line = raw.trim();
    const at = line.indexOf(OWNER_STEPS_MARKER);
    if (at >= 0) {
      const title = line
        .slice(at + OWNER_STEPS_MARKER.length)
        .trim()
        .slice(0, 200);
      current = { title: title || "Schritt aus einem Auftrag", place: "other", steps: [] };
      result.push(current);
      continue;
    }
    if (!current) continue;
    if (line === "") {
      if (current.steps.length > 0) current = null;
      continue;
    }
    const place = /^ort:\s*([a-z_]+)/i.exec(line)?.[1]?.toLowerCase();
    if (place) {
      if ((PLACES as readonly string[]).includes(place)) current.place = place as OwnerTaskPlace;
      continue;
    }
    const step = /^[-*]\s+(.+)$/.exec(line)?.[1];
    if (step && current.steps.length < MAX_STEPS) current.steps.push(step.slice(0, 400));
  }
  return result.filter((r) => r.steps.length > 0);
}
